// eslint-disable-next-line no-restricted-imports
import NextLink, { LinkProps as NextLinkProps } from 'next/link'
import { omit, pick } from 'lodash'
import * as React from 'react'
// eslint-disable-next-line custom-config/prevent-chakra-import
import { forwardRef, VisuallyHidden, Link as ChakraLink, LinkProps as ChakraLinkProps } from '@chakra-ui/react'
import { isNextJsPage } from '~/common/nextJsUtils'
import { Msg } from '~/common/localizationUtils'

// Overriding the type of `href` because Next and Chakra have slightly differing
//  opinions on what shape they should be but both agree on strings
export type LinkProps = Omit<NextLinkProps, 'href' | 'color'> &
  Omit<ChakraLinkProps, 'href'> & { href?: string; avoidNextLink?: boolean }

// These are props that are common to both next/link and Chakra's Link
const commonLinkProps = ['href']

// These are all of the props that next/link accepts
//  We list them out here so we can pick them off and only pass these to next/link
const nextLinkProps = ['as', 'replace', 'scroll', 'shallow', 'passHref', 'prefetch', 'locale', 'legacyBehavior']

interface RouteTree extends Record<string, RouteTree | boolean> {}
let nextJSUrls: RouteTree = {}

// dynamically load this file because it is not checked in to git (it's generated)
import('./nextjs_urls.json')
  .then(urlTree => {
    nextJSUrls = urlTree
  })
  .catch(() => {
    // eslint-disable-next-line no-console
    console.error('failed to load next urls in Links.tsx')
  })

export function isNextJSUrl(href: string): boolean {
  // return true
  if (!href || !href.startsWith('/')) return false

  // remove the leading '/'
  let fixedHref = href.substring(1)
  const qPos = fixedHref.indexOf('?')
  // strip off any query string
  fixedHref = qPos > -1 ? fixedHref.substring(0, qPos) : fixedHref
  const parts = fixedHref.split('/') || []
  let subtree: RouteTree = nextJSUrls
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i]
    // if we have a part then try to navigate down into that subtree
    if (part) {
      if (typeof subtree[part] === 'object') {
        // navigate down the tree
        subtree = subtree[part]
      } else {
        // check for a variable path part
        const keys = Object.keys(subtree)
        const v = keys.find(key => key !== '[leaf]' && key.startsWith('[') && key.endsWith(']'))
        if (v && typeof subtree[v] === 'object') {
          // navigate down the tree using the variable path part
          subtree = subtree[v]
        } else {
          return false
        }
      }
    }
  }

  // we have now traversed down the entire tree so see if there is a leaf node
  //   at this point that represents a page
  return !!subtree['[leaf]']
}

/**
 * Overrides Chakra's Link component wrapped in a Next.js Link when possible.
 *
 * See: https://v1.chakra-ui.com/docs/components/navigation/link
 */

NextLink.displayName = 'NextLink'
export const Link = forwardRef(({ children, avoidNextLink, ...props }: LinkProps, ref) => {
  const nextProps = pick(props, nextLinkProps.concat(commonLinkProps))
  const chakraProps = omit(props, nextLinkProps)

  if (isNextJsPage() && props.href && NextLink && !avoidNextLink) {
    // on NextJS pages check to see if we're linking to a Django page and if so shut off prefetching
    const prefetch = isNextJSUrl(props.href || '') ? {} : { prefetch: false }
    return (
      <NextLink legacyBehavior {...prefetch} {...(nextProps as NextLinkProps)} passHref>
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <ChakraLink {...chakraProps} ref={ref}>
          {children}
          {props.isExternal && (
            <VisuallyHidden>
              {' '}
              <Msg id="common.ui.opens_in_new_tab_affix" />
            </VisuallyHidden>
          )}
        </ChakraLink>
      </NextLink>
    )
  }

  return (
    <ChakraLink {...chakraProps} ref={ref}>
      {children}
      {props.isExternal && (
        <VisuallyHidden>
          {' '}
          <Msg id="common.ui.opens_in_new_tab_affix" />
        </VisuallyHidden>
      )}
    </ChakraLink>
  )
})
Link.displayName = 'Link'

export function asLink(href?: string, additionalProps = {}) {
  if (isNextJsPage() && NextLink && href) {
    // on NextJS pages check to see if we're linking to a Django page and if so shut off prefetching
    const prefetch = isNextJSUrl(href || '') ? {} : { prefetch: false }

    return { as: NextLink, href, ...prefetch, ...additionalProps }
  }

  if (href) {
    // we're on a DJango page
    return { as: 'a' as const, href, ...additionalProps }
  }

  return { ...additionalProps }
}

// Exporting the actual <Link> from Chakra because there are some cases that need it
//   like when using the `as` property with Reach Router
export { ChakraLink }
